import React, { useCallback, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "@store/index";
import {
  chartConfigSelector,
  chartConfigDispatcher,
  getCategoryRollupScope,
} from "@store/slices/chart-config-slice";
import { useGetDatasetQuestionsResponseMutation } from "@store/api/dataset-api";
import {
  significanceLevelToPValue,
  testTypeToApiFormat,
} from "@utils/statistical-testing-utils";
import { resolveVisualizationBaseFilters } from "@utils/visualization-base-search";
import { mergeBaseFiltersWithExistingFilters } from "@utils/visualization-base-utils";
import type { WidgetRollup } from "@models/Rollup";
import type { VisualizationBase } from "@models/VisualizationBase";

export type WeightSelection = "DISABLED" | "DATASET_DEFAULT" | "SURVEY_WEIGHT";

const WEIGHT_SELECTION_MAP: Record<string, WeightSelection> = {
  DISABLED: "DISABLED",
  DATASET_DEFAULT: "DATASET_DEFAULT",
  SURVEY_WEIGHT: "SURVEY_WEIGHT",
};

/**
 * Processes raw API response data into bar chart series format.
 */
export function processBarChartData(data: any[]) {
  return data.map((item: any) => {
    let value = 0;
    let valueFound = false;
    const firstSurvey = item.surveys?.[0];

    if (firstSurvey) {
      if (
        firstSurvey.descriptiveMetrics &&
        firstSurvey.descriptiveMetrics.length > 0
      ) {
        const metricValue = firstSurvey.descriptiveMetrics[0].value;
        if (metricValue !== undefined && metricValue !== null) {
          value = metricValue;
          valueFound = true;
        }
      }
      if (
        !valueFound &&
        firstSurvey.percentage !== undefined &&
        firstSurvey.percentage !== null
      ) {
        value = firstSurvey.percentage;
      }
    }

    const statSignificance =
      item.surveys?.[0]?.descriptiveMetrics?.[0]?.statSignificance ?? 0;
    const baseN =
      item.surveys?.[0]?.descriptiveMetrics?.[0]?.baseN ?? null;
    const cellN =
      item.surveys?.[0]?.descriptiveMetrics?.[0]?.cellN ?? null;

    return {
      name: item.variableLabel,
      variableId: item.variableId as string | undefined,
      data: [value],
      statSignificance,
      baseN,
      cellN,
    };
  });
}

interface UseBarChartDataReturn {
  fetchChartData: (
    weight: WeightSelection,
    shouldApplyFilters: boolean,
    overrideDates?: { startDate: string | null; endDate: string | null },
    overrideStatTestingEnabled?: boolean,
    overridePeriodCount?: number | null,
    preserveExistingStatSig?: boolean,
    overrideRollup?: WidgetRollup | null,
    overrideFocusParams?: { focusMode: string; focusEntity: string },
    overrideCategoryRollups?: WidgetRollup[] | null,
    overrideVisualizationBase?: VisualizationBase | null,
  ) => Promise<void>;
  isRefetchingData: boolean;
  weightsApplied: boolean;
  setWeightsApplied: React.Dispatch<React.SetStateAction<boolean>>;
  getDatasetQuestionsResponse: ReturnType<typeof useGetDatasetQuestionsResponseMutation>[0];
}

export function useBarChartData(): UseBarChartDataReturn {
  const dispatch = useAppDispatch();
  const { currentConfig, datasetId, questionId, activeFilters } =
    useAppSelector(chartConfigSelector);

  const [weightsApplied, setWeightsApplied] = useState<boolean>(true);

  const [getDatasetQuestionsResponse, { isLoading: isRefetchingData }] =
    useGetDatasetQuestionsResponseMutation();

  const { updateChartData, updateColorPalette, clearRollingAverageResponseFields } =
    chartConfigDispatcher(dispatch);

  const rollingAverageRef = useRef(currentConfig.rollingAverage);
  rollingAverageRef.current = currentConfig.rollingAverage;

  // Tracks the latest series so we can preserve statSignificance when stat testing
  // is enabled but dates are not included (API returns all zeros in that case).
  const currentSeriesRef = useRef(currentConfig.series);
  currentSeriesRef.current = currentConfig.series;

  const fetchChartData = useCallback(
    async (
      weight: WeightSelection,
      shouldApplyFilters: boolean,
      overrideDates?: { startDate: string | null; endDate: string | null },
      overrideStatTestingEnabled?: boolean,
      overridePeriodCount?: number | null,
      preserveExistingStatSig?: boolean,
      overrideRollup?: WidgetRollup | null,
      overrideFocusParams?: { focusMode: string; focusEntity: string },
      overrideCategoryRollups?: WidgetRollup[] | null,
      overrideVisualizationBase?: VisualizationBase | null,
    ) => {
      if (!datasetId || !questionId) return;

      let startDate: string | null | undefined =
        overrideDates !== undefined
          ? overrideDates.startDate
          : currentConfig.timePeriodSelection?.startDate;
      let endDate: string | null | undefined =
        overrideDates !== undefined
          ? overrideDates.endDate
          : currentConfig.timePeriodSelection?.endDate;

      const statTestingEnabled =
        overrideStatTestingEnabled !== undefined
          ? overrideStatTestingEnabled
          : currentConfig.statisticalTesting?.enabled;

      const explicitNoDates =
        overrideDates !== undefined &&
        !overrideDates.startDate &&
        !overrideDates.endDate;
      const reduxIsAllTime =
        currentConfig.timePeriodSelection?.timePeriod === "all_time";
      const skipDates =
        explicitNoDates || (overrideDates === undefined && reduxIsAllTime);

      if (!skipDates && !startDate && !endDate) {
        if (currentConfig.responsesMinDate && currentConfig.responsesMaxDate) {
          startDate = currentConfig.responsesMinDate;
          endDate = currentConfig.responsesMaxDate;
        }
      }

      const shouldIncludeDates = !skipDates && !!(startDate && endDate);

      clearRollingAverageResponseFields();

      try {
        const effectiveRollup = overrideRollup !== undefined ? overrideRollup : currentConfig.focusRollup;
        const effectiveFocusMode = overrideFocusParams?.focusMode ?? currentConfig.focusMode;
        const effectiveFocusEntity = overrideFocusParams?.focusEntity ?? currentConfig.focusEntity;
        const scope = getCategoryRollupScope(effectiveFocusMode);
        const effectiveCategoryRollups =
          overrideCategoryRollups !== undefined
            ? overrideCategoryRollups
            : currentConfig.categoryRollupsByScope?.[scope];
        const effectiveVisualizationBase =
          overrideVisualizationBase !== undefined
            ? overrideVisualizationBase
            : currentConfig.visualizationBase;
        const baseFilters = await resolveVisualizationBaseFilters(
          dispatch,
          datasetId,
          effectiveVisualizationBase,
        );
        const dashboardFilters =
          shouldApplyFilters && activeFilters.length > 0 ? activeFilters : [];
        const combinedFilters = mergeBaseFiltersWithExistingFilters(
          dashboardFilters,
          baseFilters,
        );
        const result = await getDatasetQuestionsResponse({
          id: questionId,
          datasetId: datasetId,
          topN: null,
          sorting: "ORIGINAL",
          weightSelection: WEIGHT_SELECTION_MAP[weight],
          chartType: "BAR",
          filters: combinedFilters.length > 0 ? combinedFilters : undefined,
          ...(effectiveRollup
            ? {
                focusRollup: effectiveRollup,
                ...(effectiveFocusMode && { focusMode: effectiveFocusMode }),
              }
            : effectiveFocusMode && effectiveFocusEntity
              ? { focusMode: effectiveFocusMode, focusEntity: effectiveFocusEntity }
              : {}),
          ...(effectiveCategoryRollups !== undefined && {
            categoryRollups: effectiveCategoryRollups ?? [],
          }),
          ...(shouldIncludeDates && {
            startDate: startDate!,
            endDate: endDate!,
          }),
          ...(statTestingEnabled &&
            shouldIncludeDates && {
              enableStatTesting: true,
              statTestingType: testTypeToApiFormat(
                currentConfig.statisticalTesting?.testType,
              ),
              pValue: significanceLevelToPValue(
                currentConfig.statisticalTesting?.significanceLevel || "90%",
              ),
              minimumSampleSize:
                currentConfig.statisticalTesting?.minimumSampleSize || 30,
            }),
          ...(() => {
            if (skipDates) return {};
            const ra = rollingAverageRef.current;
            const pc =
              overridePeriodCount !== undefined
                ? overridePeriodCount
                : ra?.enabled
                  ? ra.windowSize
                  : null;
            return pc ? { periodCount: pc } : {};
          })(),
        });

        if (result.data?.data && Array.isArray(result.data.data)) {
          const rawSeries = processBarChartData(result.data.data);

          // When preserveExistingStatSig=true (initial drawer open), always keep
          // the statSignificance values that loadConfig already wrote into Redux.
          // This ensures the preview shows the same marks as the saved dashboard
          // even when the resolved "latest_quarter" has advanced since save time
          // and the re-fetch would return zeros for the new comparison period.
          // All user-triggered refetches (time period change, stat testing toggle,
          // weight change, etc.) leave this false so they use fresh API values.
          const series = preserveExistingStatSig
            ? rawSeries.map((s) => {
                const existing = currentSeriesRef.current.find(
                  (e) => e.name === s.name,
                );
                return {
                  ...s,
                  statSignificance:
                    existing != null
                      ? ((existing as any).statSignificance ?? s.statSignificance)
                      : s.statSignificance,
                };
              })
            : rawSeries;

          // Use the maximum baseN across all data items — more representative than
          // the first element alone. Fall back to responseCount for older API responses.
          const baseNValues = (result.data.data as any[])
            .map((item: any) => item.surveys?.[0]?.descriptiveMetrics?.[0]?.baseN)
            .filter((n: any): n is number => typeof n === "number");
          const chartN: number | null =
            baseNValues.length > 0
              ? Math.max(...baseNValues)
              : (result.data?.question?.responseCount ?? null);

          updateChartData({
            categories: result.data.data.map(
              (item: any) => item.variableLabel,
            ),
            series,
            responsesMinDate: result.data?.responsesMinDate,
            responsesMaxDate: result.data?.responsesMaxDate,
            effectiveRollingPeriodCount:
              result.data?.effectiveRollingPeriodCount,
            rollingPeriodsWarningMessage:
              result.data?.rollingPeriodsWarningMessage,
            comparisonRollingLabel: result.data?.comparisonRollingLabel,
            missingRollingPeriodLabels:
              result.data?.missingRollingPeriodLabels,
            chartN,
          });

          updateColorPalette(currentConfig.colorPalette);

          if (result.data?.weightsApplied !== undefined) {
            setWeightsApplied(result.data.weightsApplied);
          }
        }
      } catch (error) {
        console.error("Error fetching chart data:", error);
      }
    },
    [
      dispatch,
      datasetId,
      questionId,
      activeFilters,
      currentConfig.statisticalTesting,
      currentConfig.timePeriodSelection,
      currentConfig.responsesMinDate,
      currentConfig.responsesMaxDate,
      currentConfig.focusMode,
      currentConfig.focusEntity,
      currentConfig.focusRollup,
      currentConfig.categoryRollupsByScope,
      currentConfig.colorPalette,
      currentConfig.visualizationBase,
      getDatasetQuestionsResponse,
      updateChartData,
      updateColorPalette,
      clearRollingAverageResponseFields,
    ],
  );

  return {
    fetchChartData,
    isRefetchingData,
    weightsApplied,
    setWeightsApplied,
    getDatasetQuestionsResponse,
  };
}
